import React, { useState, useEffect } from 'react';
import { HiOutlineCreditCard, HiOutlineRefresh, HiSearch } from 'react-icons/hi';
import { FaCreditCard, FaApple, FaWallet } from 'react-icons/fa';
import { MdPayment } from 'react-icons/md';
import { paymentsApi } from '../../../services/api/payments.api';
import { toast } from 'react-toastify';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';

// مصفوفة فارغة للبيانات الحقيقية

// أيقونات طرق الدفع
const paymentIcons = {
  credit_card: <FaCreditCard className="text-blue-500" />,
  mada: <MdPayment className="text-green-600" />,
  apple_pay: <FaApple className="text-black dark:text-white" />,
  wallet: <FaWallet className="text-purple-500" />
};

// حالة الدفع
const statusStyles = {
  // الحالات الناجحة (أخضر)
  success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  CAPTURED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  AUTHORIZED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  
  // الحالات المعلقة (أصفر)
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  INITIATED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  
  // الحالات الفاشلة (أحمر)
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  DECLINED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  TIMEDOUT: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  VOID: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  
  // الحالات المحايدة (رمادي)
  UNKNOWN: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
};

const ElectronicPayment = () => {
  // تعريف المتغيرات الأساسية أولاً
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [allPayments, setAllPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 10;
  
  // جلب البيانات من API
  const fetchPayments = async () => {
    try {
      setIsLoading(true);
      const response = await paymentsApi.getElectronicPayments();
      
      // التحقق من وجود البيانات
      if (!response) {
        setError('لم يتم استلام أي استجابة من الخادم');
        setAllPayments([]);
        return;
      }

      // التحقق من نوع البيانات
      if (Array.isArray(response)) {
        setAllPayments(response);
      } else if (response && Array.isArray(response.data)) {
        setAllPayments(response.data);
      } else {
        setError('شكل البيانات غير صحيح');
        setAllPayments([]);
      }
    } catch (err) {

      setError('حدث خطأ أثناء جلب البيانات');
      toast.error('حدث خطأ أثناء جلب البيانات');
    } finally {
      setIsLoading(false);
    }
  };
  
  // تحميل البيانات عند التحميل الأولي
  useEffect(() => {
    fetchPayments();
  }, [currentPage]);
  
  // تحديث البحث مع تأخير
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 1 || searchQuery === '') {
        setCurrentPage(1);
        fetchPayments();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // تحديث البيانات
  const handleRefresh = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPaymentMethodFilter('');
    setCurrentPage(1);
    fetchPayments();
  };
  
  // عرض اللودر الكامل فقط أول مرة أو عند عدم وجود بيانات
  if (isLoading && allPayments.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // عرض رسالة الخطأ
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {error}
      </div>
    );
  }

  // فلترة المدفوعات مع دعم حالات Tap المختلفة
  const filterPayments = (payments) => {
    if (!Array.isArray(payments)) return [];

    return payments.filter((payment) => {
      if (!payment) return false;

      // فلترة حسب البحث (آمن مع القيم غير المعرفة)
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === '' ||
        (payment.tap_payment_id && payment.tap_payment_id.toLowerCase().includes(searchTerm)) ||
        (payment.user?.name && payment.user.name.toLowerCase().includes(searchTerm)) ||
        (payment.id && payment.id.toString().includes(searchTerm));

      // فلترة حسب الحالة مع خرائط لحالات Tap
      const statusUpper = (payment.status || '').toUpperCase();
      let matchesStatus = true;
      if (statusFilter) {
        if (statusFilter === 'success') {
          matchesStatus = ['SUCCESS', 'CAPTURED', 'AUTHORIZED', 'SUCCESSFUL', 'SUCCESS'].includes(statusUpper);
        } else if (statusFilter === 'pending') {
          matchesStatus = ['PENDING', 'INITIATED', 'IN_PROGRESS'].includes(statusUpper);
        } else if (statusFilter === 'failed') {
          matchesStatus = ['FAILED', 'DECLINED', 'CANCELLED', 'TIMEDOUT', 'VOID'].includes(statusUpper);
        } else {
          // fallback للمقارنة المباشرة بحساسية أحرف أقل
          matchesStatus = statusUpper === statusFilter.toUpperCase();
        }
      }

      // فلترة حسب طريقة الدفع
      const matchesPaymentMethod = paymentMethodFilter === '' || payment.payment_method === paymentMethodFilter;

      return matchesSearch && matchesStatus && matchesPaymentMethod;
    });
  };

  // الحصول على العناصر الحالية للصفحة
  const getCurrentItems = () => {
    const filteredPayments = filterPayments(allPayments);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  };
  
  const filteredPayments = filterPayments(allPayments);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const currentItems = getCurrentItems();
  
  // معالجة تغيير البحث والتصفية
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // العودة للصفحة الأولى عند تغيير البحث
    fetchPayments(); // إعادة جلب البيانات عند تغيير البحث
  };
  
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };
  
  const handlePaymentMethodChange = (e) => {
    setPaymentMethodFilter(e.target.value);
    setCurrentPage(1);
  };
  
  // تغيير الصفحة
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0); // التمرير لأعلى الصفحة
  };

  // دالة تنسيق التاريخ والوقت
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // تحويل إلى 12 ساعة
    
    return {
      date: `${day}/${month}/${year}`,
      time: `${hours}:${minutes}${ampm}`
    };
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
        {/* رأس الصفحة */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">الدفع الإلكتروني</h1>
          <button 
            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <HiOutlineRefresh className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* شريط البحث والتصفية */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="ابحث برقم العملية أو اسم المستخدم..."
                className="input-base pr-10 w-full"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <HiSearch className="absolute left-3 top-2 h-5 w-5 text-gray-400" />
            </div>
            <div className="flex space-x-2 space-x-reverse">
              <div>
                <select 
                  className="input-base"
                  value={statusFilter}
                  onChange={handleStatusChange}
                >
                  <option value="">كل الحالات</option>
                  <option value="success">ناجح</option>
                  <option value="failed">فشل</option>
                  <option value="pending">معلق</option>
                </select>
              </div>
              <div>
                <select 
                  className="input-base"
                  value={paymentMethodFilter}
                  onChange={handlePaymentMethodChange}
                >
                  <option value="">كل طرق الدفع</option>
                  <option value="credit_card">بطاقة ائتمان</option>
                  <option value="mada">مدى</option>
                  <option value="apple_pay">آبل باي</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* جدول المدفوعات */}
        <div>
          <table className="w-full text-sm text-right text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">#</th>
                <th scope="col" className="px-6 py-3">اسم المستخدم</th>
                <th scope="col" className="px-6 py-3">رقم العملية</th>
                <th scope="col" className="px-6 py-3">المبلغ</th>
                <th scope="col" className="px-6 py-3">حالة الدفع</th>
                <th scope="col" className="px-6 py-3">طريقة الدفع</th>
                <th scope="col" className="px-6 py-3">التاريخ</th>
                <th scope="col" className="px-6 py-3">الوقت</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
               <tr>
                 <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                   لا توجد مدفوعات حالياً
                 </td>
               </tr>
             ) : currentItems.map((payment) => (
                <tr key={payment.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                    {payment.id}
                  </td>
                  <td className="px-6 py-4">
                    {payment.user.name}
                  </td>
                  <td className="px-6 py-4 font-medium">
                    <span className="font-mono text-sm">{payment.tap_payment_id}</span>
                  </td>
                  <td className="px-6 py-4 font-medium">
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <span>{payment.amount}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">ر.س</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        statusStyles[payment.status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                      title={payment.status} // إظهار الحالة الكاملة عند التحويم
                    >
                      {['success', 'CAPTURED', 'SUCCESS', 'AUTHORIZED'].includes(payment.status) && 'ناجحة'}
                      {['failed', 'FAILED', 'DECLINED', 'CANCELLED', 'TIMEDOUT', 'VOID'].includes(payment.status) && 'مرفوضة'}
                      {['pending', 'PENDING', 'INITIATED', 'IN_PROGRESS'].includes(payment.status) && 'قيد المعالجة'}
                      {![
                        'success', 'failed', 'pending',
                        'CAPTURED', 'SUCCESS', 'AUTHORIZED',
                        'FAILED', 'DECLINED', 'CANCELLED', 'TIMEDOUT', 'VOID',
                        'PENDING', 'INITIATED', 'IN_PROGRESS'
                      ].includes(payment.status) && payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-1 space-x-reverse">
                      {paymentIcons[payment.payment_method] || <FaCreditCard className="text-gray-500" />}
                      <span className="text-gray-700 dark:text-gray-300">
                        {payment.payment_method === 'credit_card' && 'بطاقة ائتمان'}
                        {payment.payment_method === 'mada' && 'مدى'}
                        {payment.payment_method === 'apple_pay' && 'آبل باي'}
                        {!['credit_card', 'mada', 'apple_pay'].includes(payment.payment_method) && payment.payment_method}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                    {formatDateTime(payment.created_at).date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                    {formatDateTime(payment.created_at).time}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* تصفح الصفحات */}
        <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-700 px-4 py-3 border-t border-gray-200 dark:border-gray-600 rounded-b-lg">
          <div className="flex items-center">
            <button 
              className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <span className="mx-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              {currentPage} / {totalPages}
            </span>
            <button 
              className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
              onClick={() => setCurrentPage(prev => prev < totalPages ? prev + 1 : prev)}
              disabled={currentPage === totalPages}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectronicPayment;